import { useState, useEffect, useCallback, useRef } from "react";
import IndoorPositioningTripwire from "../../../../api/IndoorPositioningTripwire";
import { isProvisionalTripwireId } from "../utils/tripwireUtils";

// CRUD state for the floor-plan editor's 越線 (tripwire) tool. Unlike
// useAreaPolygon (which only mutates local state and relies on the modal's
// 保存 button to persist), tripwires persist immediately through their own
// REST endpoints (spec "Frontend": "saved via the new REST endpoints, not
// the floor-plan 保存 payload") -- so every mutator here round-trips the
// backend and reconciles local state from its response.
//
// `message` is an INJECTED antd message instance (from the caller's
// `message.useMessage()`), not the static `message` import -- antd documents
// the static singleton as unsupported under this app's React 17 legacy
// `ReactDOM.render` root (src/index.js), and in practice it intermittently
// (~1 in 5) fails to render its toast at all under that combination. See
// FloorPlanModal/index.js (holds the `contextHolder`) and
// TripwireSettingsModal.jsx (same fix, shared instance) for the rest of
// this change.
export const useTripwires = (machineID, open, message) => {
  const [tripwires, setTripwires] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Per-line request sequence numbers for `update` (see below): guards
  // against an OLDER in-flight PUT resolving after a NEWER one has already
  // started for the same line (e.g. a second drag before the first PUT
  // returns) and clobbering the newer optimistic/confirmed state with its
  // own stale result.
  const updateSeqRef = useRef({});
  // Temp-id counter for optimistic creates (`create` below): a decrementing
  // negative integer per provisional line (-1, -2, ...), so it can never
  // collide with a real (positive) server id, and `isProvisionalTripwireId`
  // (tripwireUtils.js) can tell a provisional line apart from a confirmed
  // one with a single sign check.
  const tempIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!machineID) return;
    setLoading(true);
    try {
      const res = await IndoorPositioningTripwire.List(machineID);
      if (res.code === 200) {
        setTripwires(res.data?.tripwires || []);
      } else {
        message.error(res.message || "載入越線警報失敗");
        setTripwires([]);
      }
    } catch (_) {
      message.error("載入越線警報失敗");
      setTripwires([]);
    } finally {
      setLoading(false);
    }
  }, [machineID, message]);

  // Matches useStations/useSymbols: fetch only while OPEN, and otherwise
  // leave state alone. Closing the modal does NOT clear `tripwires` -- the
  // last-loaded lines stay in state so the canvas has something to draw
  // (behind the loading spinner) the instant the modal reopens, instead of
  // flashing empty until the next `load()` resolves. `load()` itself still
  // clears to [] on a failed/non-200 response (see above), and a
  // machineID change re-fires this effect (via `load`'s own dependency).
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Returns the created tripwire on success, or null (and surfaces the
  // backend's 400 message) on failure -- callers decide what to do next
  // (e.g. keep the settings modal open) based on that return value. A real
  // `catch` (not just `finally`) guarantees a toast either way: the API
  // client (IndoorPositioningTripwire.Create) already never rejects (it
  // catches axios errors itself and resolves a `{code,message}` envelope),
  // but an unguarded `try { ... } finally { ... }` with no `catch` would
  // silently drop ANY other exception (e.g. a malformed `res.data.tripwire`
  // on a 200) as an unhandled promise rejection -- no toast, no revert --
  // which is exactly the failure mode a silent "line just doesn't save"
  // report would look like from the operator's side.
  // Optimistic, mirroring `update` below (product owner: "after the second
  // point landed, the line disappears for a second then appears after saved
  // successfully"). The caller (FloorPlanModal's add-mode click handler)
  // already knows the final points the instant the operator places the
  // second point (plane) / clicks the lane (line/tunnel) -- there is
  // nothing left to wait for except the server's assigned id and defaults.
  // The add-mode rubber-band preview is torn down on that same click, so
  // waiting for the POST to resolve before showing anything is exactly what
  // left the line visibly absent for a frame; inserting a PROVISIONAL row
  // (negative temp id, see `tempIdRef` above) into state synchronously
  // closes that gap.
  //
  // `onProvisional(tempId)` fires synchronously, BEFORE this function's
  // first `await` -- an async function's body runs synchronously up to its
  // first `await`, so the callback always lands in the same tick as the
  // call, before the caller ever awaits the returned promise. Callers use
  // it to focus the provisional line immediately, then carry that focus
  // over to the real id once the POST resolves (see FloorPlanModal/
  // index.js's handleTripwireCanvasClick).
  //
  // On success the provisional row is replaced in place by the server's
  // object; on failure it is removed and the existing error toast fires
  // (same prefix as before, no success toast either way -- see `update`'s
  // comment). Both paths use a FUNCTIONAL `setTripwires` update guarded by
  // presence checks, for two DIFFERENT reasons a reload (`load()` re-firing
  // because the modal was closed and reopened while the POST was still in
  // flight) can race this response:
  //   - If the temp id is gone AND the server id is already present, the
  //     reload's own snapshot already picked up this exact line (it landed
  //     server-side before the reload's GET ran) -- do nothing, so we never
  //     insert a duplicate.
  //   - If the temp id is gone and the server id is NOT present, the reload
  //     raced ahead of the POST (its GET ran before this create landed
  //     server-side) and simply doesn't know about this line yet -- APPEND
  //     the confirmed object rather than silently dropping a creation that
  //     genuinely succeeded (a code reviewer caught an earlier version of
  //     this that treated "temp id gone" as ALWAYS meaning "don't touch
  //     state", which reintroduced this feature's own bug: a successfully
  //     created line could vanish from the canvas until the next full
  //     reload).
  //   - If the temp id is still present, replace it in place (the common,
  //     non-raced case).
  // On FAILURE the temp id is simply removed if present; there is no
  // server object to reconcile against, so no analogous "append" case.
  const create = useCallback(
    async (payload, { onProvisional } = {}) => {
      tempIdRef.current -= 1;
      const tempId = tempIdRef.current;
      const provisional = { ...payload, id: tempId };
      setTripwires((prev) => [...prev, provisional]);
      onProvisional?.(tempId);

      setSaving(true);
      try {
        const res = await IndoorPositioningTripwire.Create(machineID, payload);
        if (res.code === 200) {
          const created = res.data.tripwire;
          setTripwires((prev) => {
            if (prev.some((t) => t.id === created.id)) return prev;
            if (prev.some((t) => t.id === tempId)) {
              return prev.map((t) => (t.id === tempId ? created : t));
            }
            return [...prev, created];
          });
          // No success toast -- matches station/symbol placement's drag
          // and position-update paths, which stay silent on success and
          // only ever toast on failure (see this hook's own error paths
          // below, all kept).
          return created;
        }
        message.error(`新增失敗：${res.message || "未知錯誤"}`);
        setTripwires((prev) => prev.filter((t) => t.id !== tempId));
        return null;
      } catch (error) {
        console.error("新增越線警報時發生未預期錯誤", error);
        message.error("新增失敗：發生未預期錯誤，請稍後再試");
        setTripwires((prev) => prev.filter((t) => t.id !== tempId));
        return null;
      } finally {
        setSaving(false);
      }
    },
    [machineID, message],
  );

  // Optimistic: the caller (TripwireOverlay's drag handlers) already knows
  // the exact dropped position, so it is committed to `tripwires` state
  // IMMEDIATELY -- before the PUT resolves -- rather than only after a
  // round-trip. That is what stops a drag from visibly reverting to the
  // pre-drag position for a frame and then jumping forward once the
  // request completes. On a 400 it reverts to the pre-request snapshot and
  // surfaces the backend's message; a stale response (superseded by a newer
  // drag on the same line before this one resolved, per updateSeqRef) never
  // touches state either way -- only the LATEST request for a given line id
  // may apply its result, win or lose.
  //
  // Guards against a PROVISIONAL id (see `create` above): a line whose
  // create POST hasn't resolved yet has no server id to PUT against.
  // FloorPlanModal/index.js and TripwireOverlay already keep the UI from
  // reaching this (drag handles aren't attached, the popover's buttons are
  // disabled for a provisional line) -- this is the last line of defense so
  // a race can never send a PUT for a negative temp id.
  const update = useCallback(
    async (id, payload) => {
      if (isProvisionalTripwireId(id)) {
        console.warn(
          "useTripwires.update: ignored -- tripwire is still provisional (create not yet confirmed)",
          id,
        );
        return null;
      }
      const seq = (updateSeqRef.current[id] || 0) + 1;
      updateSeqRef.current[id] = seq;

      let previous = null;
      setTripwires((prev) => {
        const existing = prev.find((t) => t.id === id);
        if (!existing) return prev;
        previous = existing;
        return prev.map((t) => (t.id === id ? { ...t, ...payload } : t));
      });

      setSaving(true);
      try {
        const res = await IndoorPositioningTripwire.Update(
          machineID,
          id,
          payload,
        );

        if (updateSeqRef.current[id] !== seq) {
          // Superseded by a newer request for this same line -- that
          // request's result owns the final state now, not this one.
          return res.code === 200 ? res.data.tripwire : null;
        }

        if (res.code === 200) {
          const updated = res.data.tripwire;
          setTripwires((prev) => prev.map((t) => (t.id === id ? updated : t)));
          // No success toast (see `create`'s comment above) -- `update`
          // backs every drag/flip/edit-save path, so this is the same
          // silent-on-success behaviour as handleDragStop et al.
          return updated;
        }

        // Distinct zh-TW prefix (「儲存失敗：」) so a drag-drop rejection
        // reads as a real, actionable error -- not just the backend's raw
        // validation sentence with no framing -- and so it is
        // unambiguously an update failure in a page that shows several
        // different kinds of message.error toasts.
        message.error(`儲存失敗：${res.message || "未知錯誤"}`);
        if (updateSeqRef.current[id] === seq && previous) {
          const revertTo = previous;
          setTripwires((prev) => prev.map((t) => (t.id === id ? revertTo : t)));
        }
        return null;
      } catch (error) {
        // See `create`'s comment: a real `catch` (not just `finally`)
        // guarantees this always surfaces AND still reverts, instead of a
        // silent unhandled rejection that leaves the operator staring at a
        // line that "just doesn't save" with no toast at all.
        console.error("更新越線警報時發生未預期錯誤", error);
        message.error("儲存失敗：發生未預期錯誤，請稍後再試");
        if (updateSeqRef.current[id] === seq && previous) {
          const revertTo = previous;
          setTripwires((prev) => prev.map((t) => (t.id === id ? revertTo : t)));
        }
        return null;
      } finally {
        setSaving(false);
      }
    },
    [machineID, message],
  );

  // Same provisional-id guard as `update` above -- a line still awaiting its
  // create POST has no server id to DELETE.
  const remove = useCallback(
    async (id) => {
      if (isProvisionalTripwireId(id)) {
        console.warn(
          "useTripwires.remove: ignored -- tripwire is still provisional (create not yet confirmed)",
          id,
        );
        return false;
      }
      setSaving(true);
      try {
        const res = await IndoorPositioningTripwire.Delete(machineID, id);
        if (res.code === 200) {
          setTripwires((prev) => prev.filter((t) => t.id !== id));
          // No success toast (see `create`'s comment above).
          return true;
        }
        message.error(`刪除失敗：${res.message || "未知錯誤"}`);
        return false;
      } catch (error) {
        console.error("刪除越線警報時發生未預期錯誤", error);
        message.error("刪除失敗：發生未預期錯誤，請稍後再試");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [machineID, message],
  );

  return { tripwires, loading, saving, load, create, update, remove };
};
